<template>
  <div class="p-5 pt-1">
    <p class="font-semibold text-3xl">
      List Barge Line Up Report
    </p>
    <div class="mt-6">
      <div class="bg-white rounded-2xl p-6">
        <div class="flex flex-wrap flex-row items-center justify-start">
          <span class="font-semibold text-lg mr-5">Filter</span>
          <select-with-search
            class="flex-grow"
            :options="options.tugboat_name"
            placeholder="Select tugboat"
            v-model="filter.tugboat_name"
          />
          <select-with-search
            class="flex-grow ml-6"
            :options="options.route"
            placeholder="Select route"
            v-model="filter.route"
          />
          <date-picker
            class="flex-grow ml-6"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD-MMM-YYYY"
            placeholder="Trip Start"
            v-model="filter.trip_start"
            :disabled-date="disabledStartDate"
          />
          <span class="mx-3">-</span>
          <date-picker
            class="flex-grow"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD-MMM-YYYY"
            placeholder="Trip End"
            v-model="filter.trip_end"
            :disabled-date="disabledEndDate"
          />
          <vs-button
            class="button shadow-secondary ml-6"
            color="secondary"
            @click="fetchBargeReports"
          >
            Filter
          </vs-button>
        </div>
      </div>
    </div>
    <table-top
      hide-search
      :page-size-start="false"
      class="mt-6"
      @fetch="fetchBargeReports()"
    >
      <template #start>
        <vs-button
          ref="downloadButton"
          class="button shadow-secondary font-semibold vs-con-loading__container"
          color="secondary"
          :disabled="!bargeReportData.length"
          @click="downloadBargeReports"
        >
          Export Excel
        </vs-button>
      </template>
    </table-top>
    <data-table
      is-td-static
      class="mt-6"
      :table-header="bargeReportHeaders"
      :table-data="bargeReportData"
      body-height="100vh"
      :loading="isBusy"
    >
      <template #td="{ row, header }">
        {{ putOutOrFormatValue(header, row[header]) }}
      </template>
    </data-table>
    <table-bottom
      class="mt-6"
      @fetch="fetchBargeReports()"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} List Barge Line Up Report`
  },
  components: {
    DatePicker,
    SelectWithSearch,
    TableBottom,
    TableTop,
    DataTable
  },
  data() {
    return {
      isDownload: false,
      isBusy: false,
      options: {
        route: [],
        tugboat_name: []
      },
      filter: {
        route: '',
        tugboat_name: '',
        trip_start: '',
        trip_end: ''
      },
      bargeReportHeaders: [
        { key: 'tugboat_name', label: 'Tugboat' },
        { key: 'barge_name', label: 'Barge' },
        { key: 'vessel_name', label: 'Vessel' },
        { key: 'trip_number', label: 'Service Trip' },
        { key: 'term', label: 'Term' },
        { key: 'status', label: 'Status' },
        { key: 'actual_loaded', label: 'Actual Loaded' },
        { key: 'route', label: 'Route' },
        { key: 'trip_start', label: 'Trip Start (Arrive SMDS)' },
        { key: 'trip_end', label: 'Trip End (Arrive SMDR)' },
        { key: 'depart_smds', label: 'Depart SMDS' },
        { key: 'arrive_loading_port', label: 'Arrive Loading Port' },
        { key: 'loading_start', label: 'Loading Start' },
        { key: 'loading_end', label: 'Loading End' },
        { key: 'sailing_to_smd', label: 'Sailing To SMD' },
        { key: 'actual_passing_bridge', label: 'Actual Passing Bridge' },
        { key: 'arrive_unloading_port', label: 'Arrive Unloading Port' },
        { key: 'unloading_start', label: 'Unloading Start' },
        { key: 'unloading_end', label: 'Unloading End' },
        { key: 'arrive_second_unloading_port', label: 'Arrive Second Unloading' },
        { key: 'second_unloading_start', label: 'Second Unloading Start' },
        { key: 'second_unloading_end', label: 'Second Unloading End' },
        { key: 'shipment_eta', label: 'Shipment Eta' }
      ],
      bargeReportData: []
    }
  },
  computed: {
    ...mapGetters({
      bargeReports: 'BargeLineUpReport/bargeReports',
      bargeReportFile: 'BargeLineUpReport/bargeReportFile',
      routes: 'BargeLineUpReport/routes',
      tugsNames: 'BargeLineUpReport/tugsNames'
    })
  },
  watch: {
    routes: {
      handler(val) {
        this.options.route = val.map(item => ({ value: item.name, text: item.name }))
      }
    },
    tugsNames: {
      handler(val) {
        this.options.tugboat_name = val.map(item => ({ value: item.tugboat_name, text: item.tugboat_name }))
      }
    },
    bargeReports: {
      handler(val) {
        this.bargeReportData = val
      }
    },
    bargeReportFile: {
      handler(val) {
        if (val) {
          downloadUtils(val, `barge_line_up_report_${this.moment().format('DD_MMM_YYYY')}`)
          this.$store.dispatch('BargeLineUpReport/resetFile')
        }
      }
    }
  },
  mounted() {
    this.fetchTugNames()
    this.fetchRoutes()
  },
  methods: {
    async fetchRoutes() {
      await this.$store.dispatch('BargeLineUpReport/fetchRoutes')
    },
    async fetchTugNames() {
      await this.$store.dispatch('BargeLineUpReport/fetchTugNames')
    },
    async fetchBargeReports() {
      try {
        this.isBusy = true
        await this.$store.dispatch('BargeLineUpReport/fetchBargeReports',
          Object.keys(this.filter)
            .filter(key => !!this.filter[key])
            .reduce((item, key) => {
              item[key] = this.filter[key]
              return item
            }, {})
        )
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Barge Line Up Reports list, please try again')
      }
    },
    async downloadBargeReports() {
      try {
        this.isDownload = true
        this.isBusy = true
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)

        await this.$store.dispatch('BargeLineUpReport/downloadBargeReports',
          Object.keys(this.filter)
            .filter(key => !!this.filter[key])
            .reduce((item, key) => {
              item[key] = this.filter[key]
              return item
            }, {})
        )
        this.isDownload = false
        this.isBusy = false
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
      } catch (e) {
        this.isDownload = false
        this.isBusy = false
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
        this.showFailedNotification(this, 'Failed to download Barge Line Up Reports list, please try again')
      }
    },
    disabledStartDate(date) {
      return date > this.moment(this.filter.trip_end)
    },
    disabledEndDate(date) {
      return date < this.moment(this.filter.trip_start)
    },
    changeButtonState(state, container) {
      if (state) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    },
    putOutOrFormatValue(key, value) {
      switch (true) {
        case !value:
          return 'N/A'
        case key === 'trip_start':
        case key === 'trip_end':
        case key === 'depart_smds':
        case key === 'arrive_loading_port':
        case key === 'loading_start':
        case key === 'loading_end':
        case key === 'sailing_to_smd':
        case key === 'actual_passing_bridge':
        case key === 'arrive_unloading_port':
        case key === 'unloading_start':
        case key === 'unloading_end':
        case key === 'arrive_second_unloading_port':
        case key === 'second_unloading_start':
        case key === 'second_unloading_end':
        case key === 'shipment_eta':
          return this.moment(value).format('DD MMM YYYY HH:mm:ss')
        default:
          return value
      }

    }
  }
}
</script>

<style lang="scss" scoped>
.button {
  @apply py-4 #{!important};
  @apply rounded-lg;
  @apply border-2;
  @apply font-semibold;
  @apply min-w-button;
  @apply text-base;
  height: calc( 1rem * 10 / 3 );

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

/deep/.select-container {
  @apply w-form-field #{!important};
}

/deep/.bg-option-container {
  width: unset !important;
  min-width: 100% !important;
}

/deep/.bg-accent-multiselect {
  span {
    @apply truncate;
  }
}

/deep/.bg-inner-option-container {
  ul {
    li {
      span {
        white-space: nowrap;
      }
    }
  }
}
</style>
